import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Bed, Bath, Maximize, MapPin } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatPrice, formatNumber, formatCompactPrice } from '@/utils/format';
import { estimateMonthlyPayment } from '@/utils/mortgage';
import { FavoriteButton } from '@/components/common/FavoriteButton';
import { ListingImage } from '@/components/common/ListingImage';
import { TransactionTypeBadge } from '@/components/common/TransactionTypeBadge';
import { Button } from '@/components/ui/button';

export const CompareHero = ({ listing, stacked }) => {
  const [index, setIndex] = useState(0);

  if (!listing) return null;

  const photos = listing.photos?.length ? listing.photos : ['/placeholder.jpg'];
  const total = photos.length;

  const go = (e, dir) => {
    e.preventDefault();
    e.stopPropagation();
    setIndex((i) => (i + dir + total) % total);
  };

  const schools = listing.schools || [];
  const ratings = schools.map((s) => s.rating).filter((r) => r != null);
  let schoolsVal = '—';
  if (ratings.length === 1) schoolsVal = `${ratings[0]}/10`;
  else if (ratings.length > 1) schoolsVal = `${Math.min(...ratings)}/10 – ${Math.max(...ratings)}/10`;

  const estimateVal = listing.isRental ? '—' : formatCompactPrice(listing.price);
  const mortgageVal = listing.isRental
    ? '—'
    : formatCompactPrice(Math.round(estimateMonthlyPayment({ price: listing.price })));

  const rawDesc = (listing.description || listing.remarks || '').replace(/<[^>]+>/g, '');
  const descSnippet = rawDesc.length > 180 ? rawDesc.slice(0, 180).trimEnd() + '…' : rawDesc;

  const stats = [
    { label: 'Estimate', value: estimateVal },
    { label: 'Mortgage', value: mortgageVal },
    { label: 'Schools', value: schoolsVal },
    { label: 'Year Built', value: listing.yearBuilt ?? '—' },
  ];

  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
      <div className={cn('flex', stacked ? 'flex-row' : 'flex-col sm:flex-row')}>
        {/* Carousel */}
        <div className={cn(
          'relative overflow-hidden bg-slate-100',
          stacked
            ? 'w-44 sm:w-56 shrink-0 aspect-[4/3] rounded-l-2xl'
            : 'aspect-[4/3] sm:w-96 sm:shrink-0 rounded-2xl sm:rounded-none sm:rounded-l-2xl'
        )}>
          <Link to={`/listing/${listing.id}`} target="_blank" rel="noopener noreferrer" className="block h-full w-full">
            <ListingImage
              src={photos[index]}
              alt={`${listing.type} at ${listing.address}`}
              className="h-full w-full object-cover"
            />
          </Link>
          {total > 1 && (
            <>
              <button
                type="button"
                aria-label="Previous photo"
                onClick={(e) => go(e, -1)}
                className="absolute left-2 top-1/2 -translate-y-1/2 grid h-8 w-8 place-items-center rounded-full bg-white/85 text-slate-700 shadow-sm transition-opacity hover:bg-white"
              >
                <ChevronLeft size={18} />
              </button>
              <button
                type="button"
                aria-label="Next photo"
                onClick={(e) => go(e, 1)}
                className="absolute right-2 top-1/2 -translate-y-1/2 grid h-8 w-8 place-items-center rounded-full bg-white/85 text-slate-700 shadow-sm transition-opacity hover:bg-white"
              >
                <ChevronRight size={18} />
              </button>
              <span className="absolute bottom-3 left-3 rounded-full bg-black/50 px-2 py-0.5 text-xs text-white">
                {index + 1}/{total}
              </span>
            </>
          )}
        </div>

        {/* Info */}
        <div className={cn('flex-1', stacked ? 'p-4 space-y-3' : 'p-5 space-y-4')}>
          {/* Price */}
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-3xl font-semibold text-slate-900">
                  {formatPrice(listing.price, listing.isRental)}
                </p>
                <TransactionTypeBadge isRental={listing.isRental} />
              </div>
              {listing.pricePerSqft && (
                <p className="text-sm text-slate-500">${formatNumber(listing.pricePerSqft)}/Sqft</p>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <FavoriteButton id={listing.id} />
              <Button asChild size="sm" className="rounded-full">
                <Link to={`/listing/${listing.id}`} target="_blank" rel="noopener noreferrer">See Full Details</Link>
              </Button>
            </div>
          </div>

          {/* Meta row */}
          <div className="flex items-center gap-4 text-sm text-slate-600">
            <span className="inline-flex items-center gap-1">
              <Bed size={15} className="text-slate-400" />
              {listing.beds} bd
            </span>
            <span className="inline-flex items-center gap-1">
              <Bath size={15} className="text-slate-400" />
              {listing.baths} ba
            </span>
            <span className="inline-flex items-center gap-1">
              <Maximize size={15} className="text-slate-400" />
              {formatNumber(listing.sqft)} sqft
            </span>
          </div>

          {/* Address */}
          <p className="flex items-start gap-1 text-sm text-slate-600">
            <MapPin size={15} className="mt-0.5 shrink-0 text-slate-400" />
            <span>{`${[listing.streetAddress, listing.city, listing.state].filter(Boolean).join(', ')}${listing.zip ? ` ${listing.zip}` : ''}`.trim()}</span>
          </p>

          {/* Stats row */}
          <div className="flex flex-wrap gap-x-10 gap-y-2">
            {stats.map((s) => (
              <div key={s.label}>
                <p className="text-xs text-slate-500">{s.label}</p>
                <p className="text-sm font-semibold text-blue-600">{s.value}</p>
              </div>
            ))}
          </div>

          {/* Description */}
          {descSnippet && (
            <p className="text-sm text-slate-600 leading-relaxed">
              {descSnippet}{' '}
              {rawDesc.length > 180 && (
                <Link
                  to={`/listing/${listing.id}`}
                  className="text-blue-600 hover:underline font-medium"
                >
                  See more
                </Link>
              )}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};
